'use client'

/**
 * Story moves shared by the Presenter page and the stage window shortcuts
 * (plans/07 4.1, 5.2, 5.3, 5.3a, 5.7). Every call goes out as Supervity
 * support; refusals come back as the server's sentence and show as a toast.
 */

import { useCallback, useState } from 'react'
import { useRouter } from 'next/navigation'
import { toast } from '@/components/ui/toast'
import { refetchStory } from '@/lib/story'
import { showContinueNotice, type ContinueNotice, type ContinueToken } from '@/lib/autopilot'
import { setPersona } from '@/lib/persona'
import { errorText, supportRequest } from '@/components/data-manager/dm-api'
import type { ResetResult } from '@/components/data-manager/dm-types'
import type { AutopilotChange, ContinueVia, StoryAutopilot } from '@/lib/dr-types'
import { cueStage, getStageMode, safeAppHref, setStageMode, stageWindowAlive } from './stage-bus'
import type { ControlsChange, StageTarget, StepMoveResult, SupportControls, SupportStep, SupportStory } from './presenter-types'

export const RESET_PHRASE = 'RESET'
export const RESET_TOAST = 'Reset to before the earthquake'

/** "5 Acknowledgements" reads "Step 5: Acknowledgements"; "Before the earthquake" stays as it is. */
export function stepTitle(step: Pick<SupportStep, 'button'>): string {
  const match = /^(\d+[a-z]?|R)\s+(.+)$/.exec(step.button.trim())
  return match ? `Step ${match[1]}: ${match[2]}` : step.button
}

/** The number or letter of a step button ("5", "8a", "R"), for short labels. */
export function stepCode(step: Pick<SupportStep, 'button'>): string | null {
  const match = /^(\d+[a-z]?|R)\s/.exec(step.button.trim())
  return match ? match[1] : null
}

export function fetchSupportStory(): Promise<SupportStory> {
  return supportRequest<SupportStory>('GET', '/api/dr/story')
}

/** Where the stage should be now: the pushed page when there is one, else the step's own page. */
export function stageTarget(story: Pick<SupportStory, 'stage' | 'opens' | 'persona'>): StageTarget | null {
  if (story.stage && safeAppHref(story.stage.href)) return story.stage
  const href = safeAppHref(story.opens)
  return href ? { href, persona: story.persona } : null
}

function announceMove(result: StepMoveResult): void {
  const step = result.steps?.find((row) => row.step_id === result.step_id)
  const title = step ? stepTitle(step) : (result.step_label ?? result.step_id)
  const at = result.clock_min < 0 ? null : result.at_label
  toast.success(at ? `${title} · ${at}` : title)
  result.auto_decided.forEach((d) => {
    const verb = d.decision === 'approve_with_edit' ? 'approved with an edit' : 'approved'
    toast.info(`${d.item_id} ${verb} at ${d.at_label} to keep the story going`)
  })
  if (result.trigger) {
    const t = result.trigger
    const what = t.trigger_id === 'T1' ? 'Seismic event (T1)' : t.trigger_id === 'T5' ? 'SNS batch 1 (T5)' : (t.trigger_id ?? 'Trigger')
    if (t.reason) toast.warning(`${what}: ${t.reason}`)
    else toast.info(t.label ? `${what}: ${t.label}` : `${what} sent`)
  }
}

/** After any move: refresh this window's story and cue the stage tab. */
function afterChange(story: SupportStory): void {
  void refetchStory()
  const target = stageTarget(story)
  if (target) cueStage(target.href, target.persona, story.seq)
}

/** Moves to a step and says what happened (the Presenter's step buttons; Shift+Alt+Right is Continue now). */
export async function moveToStep(stepId: string): Promise<StepMoveResult> {
  const result = await supportRequest<StepMoveResult>('POST', '/api/dr/story/step', { body: { step_id: stepId } })
  announceMove(result)
  afterChange(result)
  return result
}

/** The sentence a Continue answer shows: "Continuing to Posts sensed", "Already moving on". */
export function continueText(autopilot: StoryAutopilot, nextTitle: string | null): string {
  const result = autopilot.continue_result
  if (result && !result.accepted) return 'Already moving on'
  const target = nextTitle ? `Continuing to ${nextTitle}` : 'Continuing'
  if (result?.reason === 'queued' && autopilot.due_reason === 'min_time' && typeof autopilot.due_in_seconds === 'number') {
    return `${target} in ${autopilot.due_in_seconds} s`
  }
  return target
}

/**
 * Continue (plans/10 4.2): satisfies the wait the screen shows and never
 * approves anything. The token is the step and wait from the last poll, so a
 * second press for a wait that has already moved on is ignored by the server.
 * The answer, or the server's refusal sentence, shows inline on `surface`;
 * refusals are re-thrown for callers that need them. `story` is the support
 * story after a move (null when the step did not move yet).
 */
export async function continueStory(
  token: ContinueToken,
  via: ContinueVia,
  nextTitle: string | null,
  surface: ContinueNotice['surface']
): Promise<{ autopilot: StoryAutopilot; story: SupportStory | null }> {
  try {
    const autopilot = await supportRequest<StoryAutopilot>('POST', '/api/dr/story/autopilot', {
      body: { continue: true, step_id: token.step_id, wait_index: token.wait_index, via } satisfies AutopilotChange,
    })
    showContinueNotice(continueText(autopilot, nextTitle), 'info', surface, token)
    const story = autopilot.continue_result?.reason === 'moved' ? await fetchSupportStory().catch(() => null) : null
    if (story) afterChange(story)
    else void refetchStory()
    return { autopilot, story }
  } catch (err) {
    showContinueNotice(errorText(err), 'refusal', surface, token)
    throw err
  }
}

export async function showLinkOnStage(linkId: string): Promise<SupportStory> {
  const result = await supportRequest<SupportStory>('POST', '/api/dr/story/stage', { body: { link_id: linkId } })
  afterChange(result)
  return result
}

/** The step after the current one, or null at the end of the story. */
export function nextStep(story: SupportStory | null): SupportStep | null {
  return story?.steps?.find((row) => row.state === 'next') ?? null
}

export interface StoryControlsApi {
  busy: string | null
  move: (step: SupportStep) => Promise<StepMoveResult | null>
  show: (linkId: string, label: string) => Promise<void>
  reset: (path?: string) => Promise<ResetResult | null>
  setControls: (change: ControlsChange) => Promise<SupportControls | null>
  /** `POST /api/dr/story/autopilot`: switch, pause, skip ahead or start the exercise. */
  setAutopilot: (change: AutopilotChange) => Promise<StoryAutopilot | null>
  /** Continue from the Presenter (button or Shift+Alt+Right); the answer shows in the Autopilot panel. */
  proceed: (token: ContinueToken, nextTitle: string | null, via: ContinueVia) => Promise<StoryAutopilot | null>
}

/** The busy key of an autopilot change ("autopilot:start"), so only its own button spins. */
export function autopilotBusyKey(change: AutopilotChange): string {
  return `autopilot:${Object.keys(change)[0] ?? 'change'}`
}

function autopilotToast(change: AutopilotChange, story: SupportStory | null): string {
  if (change.start) return 'Exercise started'
  if (change.advance_now) return story?.at_label ? `Skipped ahead to ${story.at_label}` : 'Skipped ahead to the next step'
  if (change.enabled !== undefined) return change.enabled ? 'Autopilot on' : 'Autopilot off: the step buttons move the story'
  if (change.paused !== undefined) return change.paused ? 'Autopilot paused' : 'Autopilot resumed'
  return 'Autopilot updated'
}

/**
 * The Presenter's actions with busy state, toasts and the single-window
 * fallback: without a stage window, a move opens the step's page here.
 */
export function useStoryControls(onChanged?: () => void): StoryControlsApi {
  const router = useRouter()
  const [busy, setBusy] = useState<string | null>(null)

  const openHere = useCallback(
    (target: StageTarget | null) => {
      if (!target) return
      if (stageWindowAlive()) {
        if (getStageMode() === 'single') setStageMode(null)
        return
      }
      setStageMode('single')
      setPersona(target.persona)
      router.push(target.href)
    },
    [router]
  )

  const move = useCallback(
    async (step: SupportStep) => {
      setBusy(`step:${step.step_id}`)
      try {
        const result = await moveToStep(step.step_id)
        onChanged?.()
        openHere(stageTarget(result))
        return result
      } catch (err) {
        toast.error(errorText(err))
        return null
      } finally {
        setBusy(null)
      }
    },
    [onChanged, openHere]
  )

  const show = useCallback(
    async (linkId: string, label: string) => {
      setBusy(`stage:${linkId}`)
      try {
        const result = await showLinkOnStage(linkId)
        toast.success(`Showing ${label} on the stage`)
        onChanged?.()
        openHere(stageTarget(result))
      } catch (err) {
        toast.error(errorText(err))
      } finally {
        setBusy(null)
      }
    },
    [onChanged, openHere]
  )

  const reset = useCallback(
    async (path = '/api/dr/story/reset') => {
      setBusy('reset')
      try {
        const result = await supportRequest<ResetResult>('POST', path, {
          body: { confirm: RESET_PHRASE, restore_policy_controls: true },
        })
        toast.success(RESET_TOAST)
        const story = await fetchSupportStory().catch(() => null)
        if (story) afterChange(story)
        else void refetchStory()
        onChanged?.()
        return result
      } catch (err) {
        toast.error(errorText(err))
        return null
      } finally {
        setBusy(null)
      }
    },
    [onChanged]
  )

  const setControls = useCallback(
    async (change: ControlsChange) => {
      setBusy('controls')
      try {
        const result = await supportRequest<SupportControls>('POST', '/api/dr/story/controls', { body: change })
        void refetchStory()
        onChanged?.()
        return result
      } catch (err) {
        toast.error(errorText(err))
        return null
      } finally {
        setBusy(null)
      }
    },
    [onChanged]
  )

  const setAutopilot = useCallback(
    async (change: AutopilotChange) => {
      setBusy(autopilotBusyKey(change))
      try {
        const result = await supportRequest<StoryAutopilot>('POST', '/api/dr/story/autopilot', { body: change })
        const moved = !!(change.start || change.advance_now)
        const story = moved ? await fetchSupportStory().catch(() => null) : null
        if (story) {
          afterChange(story)
          openHere(stageTarget(story))
        } else {
          void refetchStory()
        }
        toast.success(autopilotToast(change, story))
        onChanged?.()
        return result
      } catch (err) {
        toast.error(errorText(err))
        return null
      } finally {
        setBusy(null)
      }
    },
    [onChanged, openHere]
  )

  const proceed = useCallback(
    async (token: ContinueToken, nextTitle: string | null, via: ContinueVia) => {
      setBusy(autopilotBusyKey({ continue: true }))
      try {
        const { autopilot, story } = await continueStory(token, via, nextTitle, 'panel')
        if (story) openHere(stageTarget(story))
        onChanged?.()
        return autopilot
      } catch {
        // The refusal sentence is already shown in the panel.
        return null
      } finally {
        setBusy(null)
      }
    },
    [onChanged, openHere]
  )

  return { busy, move, show, reset, setControls, setAutopilot, proceed }
}
